'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useAdMesh } from '../hooks/useAdMesh';
import { logger } from '../utils/logger';

export interface WeaveFallbackRecommendationsProps {
  /** Recommendation format for fallback display
   *
   * - 'product': Display as product cards
   * - 'tail': Display as tail format (default)
   */
  format?: 'product' | 'tail';

  /** Optional callback on error */
  onError?: (error: Error) => void;

  /**
   * Message ID for per-message recommendations.
   *
   * This is the primary identifier for fetching recommendations for a specific message.
   */
  messageId: string;

  /**
   * User query text for generating recommendations.
   *
   * This is the query that will be used to fetch recommendations from the backend.
   * Typically the user's question or search query.
   *
   * IMPORTANT: This should be a non-empty string. If not provided or empty,
   * the component will skip rendering to prevent 400 Bad Request errors.
   * The backend requires a valid query parameter.
   */
  query?: string;

  /**
   * Fallback state - controls whether to show recommendations
   * When true, recommendations will be fetched and displayed
   * When false or undefined, component will not render
   */
  fallback?: boolean;

  /**
   * Previously fetched recommendations from WeaveAdFormatContainer.
   * If provided and not empty, these will be rendered directly without making a new API call.
   * Only makes a new API call if this is empty/null/undefined.
   */
  previousRecommendations?: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
}

/**
 * WeaveFallbackRecommendations - Weave Format Fallback Component
 *
 * Displays recommendations as a fallback UI when no AdMesh links are detected
 * in the LLM response. Works independently without requiring WeaveAdFormatContext.
 *
 * This component will:
 * - Accept messageId, query, and fallback state as props
 * - Only render when fallback prop is true
 * - If previousRecommendations are provided and not empty: renders them directly (no API call)
 * - If previousRecommendations are empty/null: calls SDK's showRecommendations() to fetch recommendations
 * - Display recommendations in the specified format (tail or product)
 *
 * IMPORTANT: Pass previousRecommendations from WeaveAdFormatContainer to avoid duplicate API calls.
 *
 * @example
 * ```tsx
 * const [fallback, setFallback] = useState(false);
 *
 * <AdMeshProvider apiKey={apiKey} sessionId={sessionId}>
 *   <WeaveAdFormatContainer
 *     messageId={message.id}
 *     query={userQuery}
 *     onFallbackChange={setFallback}
 *   >
 *     {llmResponseContent}
 *   </WeaveAdFormatContainer>
 *   <WeaveFallbackRecommendations
 *     messageId={message.id}
 *     query={userQuery}
 *     format="tail"
 *     fallback={fallback}
 *     previousRecommendations={recommendations} // Pass from WeaveAdFormatContainer
 *   />
 * </AdMeshProvider>
 * ```
 */
export const WeaveFallbackRecommendations: React.FC<WeaveFallbackRecommendationsProps> = ({
  format = 'tail',
  onError,
  messageId,
  query,
  fallback,
  previousRecommendations,
}) => {
  const { sdk, sessionId, theme, apiKey, language, location, userId, model, messages } = useAdMesh();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerId, setContainerId] = useState<string>('');

  // Generate container ID based on message ID
  useEffect(() => {
    if (messageId) {
      setContainerId(`admesh-weave-fallback-${messageId}`);
    }
  }, [messageId]);

  // Log component render
  logger.log('[WeaveFallbackRecommendations] 🎨 Component render');

  useEffect(() => {
    // Log what we're receiving
    logger.log('[WeaveFallbackRecommendations] 🔄 useEffect triggered', {
      fallback,
      hasPreviousRecommendations: previousRecommendations && previousRecommendations.length > 0,
      previousRecommendationsCount: previousRecommendations?.length || 0
    });

    // Skip if fallback is not true
    if (!fallback) {
      logger.log('[WeaveFallbackRecommendations] ⏭️  Skipping - fallback is FALSE, not rendering recommendations');
      return;
    }

    logger.log('[WeaveFallbackRecommendations] ✅ fallback is TRUE, proceeding with recommendations');

    // Validate required parameters
    if (!messageId || !query || query.trim() === '') {
      logger.log('[WeaveFallbackRecommendations] ❌ Validation failed - returning early');
      return;
    }

    logger.log('[WeaveFallbackRecommendations] ✅ Validation passed');

    if (!sdk || !containerId) {
      logger.log('[WeaveFallbackRecommendations] SDK or containerId not ready');
      return;
    }

    // Check if we have previous recommendations to reuse
    const hasPreviousRecs = previousRecommendations && previousRecommendations.length > 0;
    
    logger.log('[WeaveFallbackRecommendations] 📊 Checking previous recommendations:', {
      hasPreviousRecs,
      count: previousRecommendations?.length || 0,
      fallback,
      messageId
    });
    
    if (hasPreviousRecs) {
      // Use previous recommendations - render directly without API call
      logger.log(`[WeaveFallbackRecommendations] ♻️  Reusing ${previousRecommendations.length} previous recommendation(s), skipping API call`);
      
      const renderRecommendations = async () => {
        try {
          const aipResponse = previousRecommendations[0];
          
          logger.log('[WeaveFallbackRecommendations] 📦 Processing AIP response:', {
            hasAipResponse: !!aipResponse,
            recommendationId: aipResponse?.recommendation_id || '',
            format: aipResponse?.recommendation?.format || 'unknown'
          });
          
          // Development logging: Log full recommendation details
          if (aipResponse && process.env.NODE_ENV === 'development') {
            logger.log('[WeaveFallbackRecommendations] 📋 Recommendation received (DEV):', {
              recommendation_id: aipResponse?.recommendation_id,
              click_url:
                aipResponse?.tracking?.click_url ||
                aipResponse?.recommendation?.creative?.landing_page_url,
              format: aipResponse?.recommendation?.format,
              full_response: aipResponse
            });
          }
          
          if (!aipResponse) {
            logger.warn('[WeaveFallbackRecommendations] ⚠️ Previous recommendation is empty, falling back to API call');
            await sdk.showRecommendations({
              query: query.trim(),
              containerId,
              sessionId,
              messageId: messageId,
            });
            return;
          }

          const sdkAny = sdk as any;
          const getRendererMethod = sdkAny.getRenderer;
          const getTrackerMethod = sdkAny.getTracker;
          const sdkTheme = sdkAny.config?.theme;

          if (getRendererMethod && getTrackerMethod) {
            const response = {
              sessionId,
              messageId,
              recommendations: [aipResponse]
            };

            // Get renderer and tracker
            const renderer = getRendererMethod.call(sdk);
            const tracker = getTrackerMethod.call(sdk);
            
            // Get apiBaseUrl from SDK instance
            const apiBaseUrl = (sdk as any)?.apiBaseUrl || 
                              (typeof window !== 'undefined' && (window as any).__ADMESH_API_BASE_URL__) ||
                              'https://api.useadmesh.com';
            
            // Render directly - pass all required props for AdMeshProvider
            await renderer.render({
              containerId,
              response,
              theme: theme || sdkTheme,
              tracker: tracker,
              sessionId: sessionId,
              apiKey: apiKey || (sdk as any)?.config?.apiKey,
              apiBaseUrl: apiBaseUrl,
              language: language,
              location: location,
              userId: userId,
              model: model,
              messages: messages,
            });
            
            logger.log('[WeaveFallbackRecommendations] ✅ Recommendations rendered from previous response (no API call)');
          } else {
            // SDK methods not accessible - fall back to showRecommendations
            // This will use the cached result from auction coordination, so it's still fast
            logger.warn('[WeaveFallbackRecommendations] ⚠️ SDK internal methods not accessible, using showRecommendations (will use cached result)');
            await sdk.showRecommendations({
              query: query.trim(),
              containerId,
              sessionId,
              messageId: messageId,
            });
          }
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          logger.error(`[WeaveFallbackRecommendations] ❌ Error rendering previous recommendations: ${err.message}`);
          // Fallback to API call on error (will use cached result)
          try {
            await sdk.showRecommendations({
              query: query.trim(),
              containerId,
              sessionId,
              messageId: messageId,
            });
          } catch (fallbackError) {
            onError?.(err);
          }
        }
      };

      renderRecommendations();
    } else {
      // No previous recommendations - make new API call
      logger.log('[WeaveFallbackRecommendations] 📤 No previous recommendations, calling sdk.showRecommendations');
      
      const fetchRecommendations = async () => {
        try {
          if (!sdk?.showRecommendations) {
            logger.log('[WeaveFallbackRecommendations] SDK showRecommendations not available');
            return;
          }

          await sdk.showRecommendations({
            query: query.trim(),
            containerId,
            sessionId,
            messageId: messageId, // REQUIRED: messageId must be provided by platform
          });

          logger.log('[WeaveFallbackRecommendations] ✅ Recommendations displayed successfully');
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          logger.log(`[WeaveFallbackRecommendations] ❌ Error: ${err.message}`);
          onError?.(err);
        }
      };

      fetchRecommendations();
    }
  }, [sdk, sessionId, containerId, format, messageId, query, fallback, onError, previousRecommendations, theme]);

  // Don't render anything if fallback is not active or validation fails
  if (!fallback || !messageId || !query || query.trim() === '') {
    return null;
  }

  // Render the container where fallback recommendations will be displayed
  // Use display: 'none' initially to prevent taking up space until recommendations are loaded
  // The SDK will set display: 'block' when it renders content
  return (
    <div
      ref={containerRef}
      id={containerId}
      className="admesh-weave-fallback-recommendations-container"
      style={{
        marginTop: '1rem',
        display: 'none', // Hidden by default, SDK will show when recommendations are rendered
      }}
    />
  );
};

export default WeaveFallbackRecommendations;
